import React, { useState } from 'react';
import { MapPin, Lock, ArrowLeft, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../utils/toast';
import RotatingBackground from '../components/RotatingBackground';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <RotatingBackground showCaption={false} interval={8000}>
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="w-full max-w-md bg-black/50 backdrop-blur-xl border border-white/20 rounded-2xl p-8 text-center">
            <h1 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Invalid Reset Link</h1>
            <p className="text-sm text-white/60 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>This password reset link is invalid or has expired.</p>
            <Button onClick={() => navigate('/login')} data-testid="back-to-login-btn" className="bg-[var(--md3-primary)] hover:bg-[var(--md3-primary-hover)] text-white rounded-xl px-6 py-3">
              Back to Sign In
            </Button>
          </div>
        </div>
      </RotatingBackground>
    );
  }

  if (success) {
    return (
      <RotatingBackground showCaption={false} interval={8000}>
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="w-full max-w-md bg-black/50 backdrop-blur-xl border border-white/20 rounded-2xl p-8 text-center">
            <CheckCircle2 size={48} className="text-green-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Password Reset!</h1>
            <p className="text-sm text-white/60 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>Your password has been updated. You can now sign in with your new password.</p>
            <Button onClick={() => navigate('/login')} data-testid="go-to-login-btn" className="bg-[var(--md3-primary)] hover:bg-[var(--md3-primary-hover)] text-white rounded-xl px-6 py-3">
              Sign In
            </Button>
          </div>
        </div>
      </RotatingBackground>
    );
  }

  return (
    <RotatingBackground showCaption={false} interval={8000}>
      <div className="min-h-screen flex items-center justify-center px-4" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate('/login')}
            data-testid="reset-back-btn"
            className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <ArrowLeft size={18} />
            <span className="text-sm">Back to Sign In</span>
          </button>

          <div className="bg-black/50 backdrop-blur-xl border border-white/20 rounded-2xl p-6 compact:p-8">
            <div className="flex items-center justify-center gap-2 mb-6">
              <MapPin className="text-white" size={28} />
              <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>iGo</span>
            </div>

            <h1 className="text-xl compact:text-2xl font-bold text-white text-center mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Choose a New Password
            </h1>
            <p className="text-sm text-white/60 text-center mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
              Enter your new password below.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                  className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-11 focus:border-[var(--md3-primary)] focus:ring-[var(--md3-primary)]"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  data-testid="reset-confirm-password-input"
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-11 focus:border-[var(--md3-primary)] focus:ring-[var(--md3-primary)]"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>

              <p className="text-xs text-white/40" style={{ fontFamily: 'Inter, sans-serif' }}>
                Must be at least 6 characters
              </p>

              <Button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-btn"
                className="w-full bg-[var(--md3-primary)] hover:bg-[var(--md3-primary-hover)] text-white font-semibold py-5 rounded-xl transition-all"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </RotatingBackground>
  );
};

export default ResetPasswordPage;
